import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Modal, FlatList, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { SUPPORTED_LANGUAGES } from '../../../components/LanguagePicker';
import { t } from '../../../i18n/translations';
import { useLanguageStore } from '../../quiz-core/languageStore';

interface OnboardingLanguageSelectorProps {
  style?: any;
}

const OnboardingLanguageSelector: React.FC<OnboardingLanguageSelectorProps> = ({ style }) => {
  const { language, setLanguage } = useLanguageStore();
  const [isOpen, setIsOpen] = useState(false);
  const insets = useSafeAreaInsets();

  const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === language);

  const handleLanguageSelect = (langCode: string) => {
    setLanguage(langCode);
    setIsOpen(false);
    
    // Save to localStorage for pre-auth experience
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      localStorage.setItem('user_selected_language', langCode);
    }
  };

  const LanguageItem = ({ item }: { item: typeof SUPPORTED_LANGUAGES[0] }) => (
    <TouchableOpacity
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#E8E5F0',
      }}
      onPress={() => handleLanguageSelect(item.code)}
    >
      <Text style={{ fontSize: 18, marginRight: 12 }}>{getFlagEmoji(item.code)}</Text>
      <Text style={{
        color: language === item.code ? '#1D1A23' : '#464555',
        fontSize: 16,
        fontWeight: language === item.code ? '600' : '400',
      }}>
        {item.nativeName}
      </Text>
      {language === item.code && (
        <Text style={{ color: '#4F46E5', marginLeft: 'auto', fontSize: 16 }}>✓</Text>
      )}
    </TouchableOpacity>
  );

  return (
    <View style={[{
      position: 'absolute',
      // Below the status bar / Dynamic Island: a fixed 18pt put the pill UNDER the island on
      // notched iPhones, hiding the globe and the start of the label.
      top: Platform.OS === 'web' ? 20 : Math.max(insets.top, 18) + 6,
      right: 16,
      zIndex: 1000,
    }, style]}>
      {/* Trigger Button */}
      <TouchableOpacity
        onPress={() => setIsOpen(true)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: 20,
          backgroundColor: '#FFFFFF',
          borderWidth: 1.5,
          borderColor: '#C7C4D8',
          shadowColor: '#4F46E5',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 4,
          elevation: 5,
        }}
        accessibilityLabel={`${t('coreUI.currentLanguage', language)}: ${currentLanguage?.nativeName}. ${t('coreUI.tapToChangeLanguage', language)}`}
        accessibilityRole="button"
      >
        <Text style={{ fontSize: 16, marginRight: 6 }}>🌐</Text>
        <Text style={{ fontSize: 14, marginRight: 6 }}>{getFlagEmoji(language)}</Text>
        <Text
          numberOfLines={1}
          style={{
            color: '#1D1A23',
            fontSize: 14,
            // Explicit line box + a hair of horizontal slack: without them the accent on "ñ"
            // and the last letter were clipped ("Espanol"), because the label was measured
            // against a font metric that differed from the one painted.
            lineHeight: 20,
            paddingHorizontal: 2,
            fontWeight: '600',
          }}
        >
          {currentLanguage?.nativeName}
        </Text>
        <Text style={{ color: '#464555', marginLeft: 4, fontSize: 12 }}>▼</Text>
      </TouchableOpacity>

      {/* Language Selection Modal */}
      <Modal
        visible={isOpen}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <TouchableOpacity
          style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          activeOpacity={1}
          onPress={() => setIsOpen(false)}
        >
          <View
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: 16,
              borderWidth: 1.5,
              borderColor: '#C7C4D8',
              maxHeight: '70%',
              width: '80%',
              maxWidth: 320,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.4,
              shadowRadius: 16,
              elevation: 10,
            }}
            onStartShouldSetResponder={() => true}
          >
            {/* Header */}
            <View style={{
              paddingHorizontal: 16,
              paddingVertical: 16,
              borderBottomWidth: 1,
              borderBottomColor: '#E8E5F0',
            }}>
              <Text style={{
                color: '#1D1A23',
                fontSize: 18,
                fontWeight: '600',
                textAlign: 'center',
              }}>
                {getChooseLanguageText(language)}
              </Text>
            </View>

            {/* Language List */}
            <FlatList
              data={SUPPORTED_LANGUAGES}
              keyExtractor={(item) => item.code}
              renderItem={LanguageItem}
              style={{ maxHeight: 400 }}
              showsVerticalScrollIndicator={false}
            />
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// Helper function to get flag emoji for language codes
const getFlagEmoji = (langCode: string): string => {
  const flagMap: Record<string, string> = {
    en: '🇺🇸',
    es: '🇪🇸', 
    fr: '🇫🇷',
    de: '🇩🇪',
    pt: '🇧🇷',
    ru: '🇷🇺',
    tl: '🇵🇭',
    it: '🇮🇹',
    am: '🇪🇹',
    ko: '🇰🇷',
  };
  return flagMap[langCode] || '🌐';
};

// Helper function to get "Choose Language" text in current language
const getChooseLanguageText = (langCode: string): string => {
  const textMap: Record<string, string> = {
    en: 'Choose Language',
    es: 'Elegir Idioma',
    fr: 'Choisir la Langue',
    de: 'Sprache Wählen',
    pt: 'Escolher Idioma',
    ru: 'Выбрать Язык',
    tl: 'Piliin ang Wika',
    it: 'Scegli Lingua',
    am: 'ቋንቋ ምረጥ',
    ko: '언어 선택',
  };
  return textMap[langCode] || 'Choose Language';
};

export default OnboardingLanguageSelector;
